import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useChatSocket } from "./useChatSocket";
import GuestInfoModal from "../components/GuestInfoModal";
import ChatMessages from "./ChatMessages";
import ChatInput from "./ChatInput";
import "./ChatArea.css";
import {
  introductionText,
  introductionTextMobile,
  REQUIRE_GUEST_INFO,
  MAX_GUEST_QUERIES,
  API_BASE_URL,
} from "../config";


function getGuestId() {
  let guestId = localStorage.getItem("guestId");
  if (!guestId) {
    const randomNumber = Math.floor(1000000000 + Math.random() * 9000000000);
    guestId = "guest" + randomNumber;
    localStorage.setItem("guestId", guestId);
  }
  return guestId;
}

const ChatArea = ({
  chatHistory = [],
  setChatHistory = () => {},
  selectedExample,
  clearSelectedExample,
  urlMobile,
}) => {
  // Retrieve both user and fetchGuestToken from AuthContext.
  const { user, fetchGuestToken, authFetch } = useAuth();
  const userId = user && user.email ? user.email : getGuestId();
  const isGuest = userId.startsWith("guest");

  const [streaming, setStreaming] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [guestQueryCount, setGuestQueryCount] = useState(0);
  const [guestInfoAvailable, setGuestInfoAvailable] = useState(false);
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [chatAreaReady, setChatAreaReady] = useState(false);
  const [qProcessed, setQProcessed] = useState(false);
  const [hasSentMessage, setHasSentMessage] = useState(false);

  // Retrieve guest info on mount.
  useEffect(() => {
    if (urlMobile) {
      localStorage.setItem("guestMobile", urlMobile);
      setGuestInfoAvailable(true);
    }else{
    const guestName = localStorage.getItem("guestName");
    const guestMobile = localStorage.getItem("guestMobile");
    setGuestInfoAvailable(Boolean(guestName && guestMobile));
    }
  }, [urlMobile]);

  // New: If user is not logged in (i.e. guest) and no guest token exists, fetch one.
  useEffect(() => {
    if (!user) {
      if (!localStorage.getItem("guestToken")) {
        const guestId = getGuestId();
        fetchGuestToken(guestId)
          .then((data) => {
            console.log("Guest token fetched:", data);
          })
          .catch((error) => {
            console.error("Error fetching guest token:", error);
          });
      }
    }
  }, [user, fetchGuestToken]);

  const { ws, connectWebSocket, waitForConnection } = useChatSocket(
    setChatHistory,
    setStreaming,
    setIsThinking
  );

  // Track mobile or desktop based on width
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Optional: If you want to do something on focus/blur for the mobile textarea
  useEffect(() => {
    if (isMobile) {
      const textArea = document.querySelector(".chat-textarea");
      if (!textArea) return;

      const handleFocus = () => {
        console.log("Textarea focused (iOS Safari).");
      };
      const handleBlur = () => {
        console.log("Textarea blurred (iOS Safari).");
      };

      textArea.addEventListener("focus", handleFocus);
      textArea.addEventListener("blur", handleBlur);

      return () => {
        textArea.removeEventListener("focus", handleFocus);
        textArea.removeEventListener("blur", handleBlur);
      };
    }
  }, [isMobile]);

  // Load chat history from localStorage.
  useEffect(() => {
    const key = `chatHistory_${userId}`;
    const localHistoryStr = localStorage.getItem(key);
    let localHistory = [];
    if (localHistoryStr) {
      try {
        localHistory = JSON.parse(localHistoryStr);
      } catch (error) {
        console.error("Error parsing local history:", error);
        localHistory = [];
      }
    }

    if (Array.isArray(localHistory) && localHistory.length > 0) {
      setChatHistory(localHistory);
    } else {
      const defaultMsg =
        window.innerWidth < 768 ? introductionTextMobile : introductionText;
      const defaultHistory = [{ role: "system", text: defaultMsg }];
      if (userId.toLowerCase() !== "guest@anonymous") {
        authFetch(`${API_BASE_URL}/api/chat-history?user_id=${userId}`)
          .then((response) => response.json())
          .then((data) => {
            let history =
              data.success && data.chatHistory ? data.chatHistory : [];
            history = [{ role: "system", text: defaultMsg }, ...history];
            setChatHistory(history);
            localStorage.setItem(key, JSON.stringify(history));
          })
          .catch((err) => {
            console.error("Error fetching chat history:", err);
            setChatHistory(defaultHistory);
            localStorage.setItem(key, JSON.stringify(defaultHistory));
          });
      } else {
        setChatHistory(defaultHistory);
        localStorage.setItem(key, JSON.stringify(defaultHistory));
      }
    }
    setChatAreaReady(true);
  }, [userId, setChatHistory]);

  const persistChatHistory = useCallback(() => {
    const key = `chatHistory_${userId}`;
    localStorage.setItem(key, JSON.stringify(chatHistory));
    console.log("Persisted chatHistory for", userId, chatHistory);
  }, [chatHistory, userId]);

  useEffect(() => {
    if (hasSentMessage && !streaming && !isThinking) {
      persistChatHistory();
    }
  }, [streaming, isThinking, persistChatHistory, hasSentMessage]);

  const sendMessage = useCallback(
    (messageText) => {
      if (streaming) {
        console.warn(
          "Please wait, the AI is still processing your previous message."
        );
        return;
      }
      const messageToSend = messageText.trim();
      if (messageToSend === "") {
        console.warn("Message cannot be empty.");
        return;
      }
      if (REQUIRE_GUEST_INFO && isGuest  && !urlMobile) {
        if (!guestInfoAvailable) {
          if (MAX_GUEST_QUERIES === 0 || guestQueryCount >= MAX_GUEST_QUERIES) {
            setShowGuestModal(true);
            return;
          } else {
            setGuestQueryCount((prev) => prev + 1);
          }
        }
      }
      const actuallySendMessage = () => {
        setHasSentMessage(true);
        setChatHistory((prev) => [...prev, { role: "user", text: messageToSend }]);
        const payload = {
          user_id: userId,
          user_input: messageToSend,
        };
        if (isMobile) {
          payload.device = "mobile";
        }
        const storedName = localStorage.getItem("guestName");
        const storedMobile = localStorage.getItem("guestMobile");
        if (storedName && storedMobile) {
          payload.name = storedName;
          payload.mobile = storedMobile;
        }
        ws.current.send(JSON.stringify(payload));
        setStreaming(true);
        setIsThinking(true);
      };
      if (
        !ws.current ||
        ws.current.readyState === WebSocket.CLOSED ||
        ws.current.readyState === WebSocket.CLOSING
      ) {
        console.warn("WebSocket is not connected. Attempting to reconnect...");
        connectWebSocket();
        waitForConnection()
          .then(actuallySendMessage)
          .catch((err) => {
            console.error(
              "WebSocket connection not established after waiting:",
              err
            );
            setChatHistory((prev) => [
              ...prev,
              {
                role: "error",
                text: "Currently connection problem. Please try after sometime!!!",
              },
            ]);
            setStreaming(false);
            setIsThinking(false);
          });
      } else if (ws.current.readyState === WebSocket.CONNECTING) {
        waitForConnection()
          .then(actuallySendMessage)
          .catch((err) => {
            console.error("WebSocket connection not established:", err);
            setChatHistory((prev) => [
              ...prev,
              {
                role: "error",
                text: "Currently connection problem. Please try after sometime!!!",
              },
            ]);
            setStreaming(false);
            setIsThinking(false);
          });
      } else if (ws.current.readyState === WebSocket.OPEN) {
        actuallySendMessage();
      }
    },
    [
      userId,
      connectWebSocket,
      waitForConnection,
      ws,
      setChatHistory,
      streaming,
      isGuest,
      guestQueryCount,
      guestInfoAvailable,
      isMobile,
    ]
  );

  // Check if there's a "q" query parameter in the URL
  useEffect(() => {
    if (!chatAreaReady || qProcessed) return;
    const params = new URLSearchParams(window.location.search);
    const query = params.get("q");
    if (query && query.trim() !== "") {
      sendMessage(query);
      setQProcessed(true);
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, [sendMessage, chatAreaReady, qProcessed]);

  // If an example was selected from the UI, send it as a message
  useEffect(() => {
    if (selectedExample) {
      if (streaming) {
        console.warn(
          "Please wait, the AI is still processing your previous message."
        );
        clearSelectedExample();
      } else if (
        typeof selectedExample === "string" &&
        selectedExample.trim() !== ""
      ) {
        sendMessage(selectedExample);
        clearSelectedExample();
      } else {
        clearSelectedExample();
      }
    }
  }, [selectedExample, sendMessage, clearSelectedExample, streaming]);

  return (
    <div className="chat-wrapper">
      {showGuestModal && (
        <GuestInfoModal
          onSubmit={({ name, mobile }) => {
            localStorage.setItem("guestName", name);
            localStorage.setItem("guestMobile", mobile);
            setGuestInfoAvailable(true);
            setShowGuestModal(false);
            setGuestQueryCount(0);
          }}
        />
      )}
      <ChatMessages
        chatHistory={chatHistory}
        isThinking={isThinking}
        streaming={streaming}
        isMobile={isMobile}
      />
      {isMobile ? (
        <div className="chat-input-fixed">
          <ChatInput onSend={sendMessage} streaming={streaming} />
        </div>
      ) : (
        <ChatInput onSend={sendMessage} streaming={streaming} />
      )}
    </div>
  );
};

export { getGuestId };
export default ChatArea;
